import { Check, CirclePlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  PACKAGING_ITEMS_LAST_UPDATED_SCOPES,
  PACKAGING_ITEMS_LAST_UPDATED_WINDOWS,
  type PackagingItemsLastUpdatedFilter,
} from '@/shared/packagingItemsLastUpdatedFilter'

type PackagingItemsLastUpdatedFacetedFilterProps = {
  value: PackagingItemsLastUpdatedFilter | undefined
  onValueChange: (value: PackagingItemsLastUpdatedFilter | undefined) => void
}

export function PackagingItemsLastUpdatedFacetedFilter({
  value,
  onValueChange,
}: PackagingItemsLastUpdatedFacetedFilterProps) {
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const title = t('packagingSuppliers:packagingItems.lastUpdatedFilterLabel')
  const isFiltered = value != null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='h-8 border-dashed'>
          <CirclePlus className='size-4' />
          {title}
          {isFiltered && (
            <span
              className='ms-1.5 size-1.5 shrink-0 rounded-full bg-primary'
              aria-hidden
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[260px] p-0' align='start'>
        <Command>
          <CommandList>
            {PACKAGING_ITEMS_LAST_UPDATED_SCOPES.map((scope, scopeIndex) => (
              <div key={scope}>
                {scopeIndex > 0 && <CommandSeparator />}
                <CommandGroup
                  heading={t(
                    `packagingSuppliers:packagingItems.lastUpdatedFilterScope.${scope}`
                  )}
                >
                  {PACKAGING_ITEMS_LAST_UPDATED_WINDOWS.map((window) => {
                    const filter = `${scope}:${window}` as PackagingItemsLastUpdatedFilter
                    const isSelected = value === filter
                    return (
                      <CommandItem
                        key={filter}
                        onSelect={() => {
                          onValueChange(filter)
                        }}
                      >
                        <div
                          className={cn(
                            'flex size-4 items-center justify-center rounded-sm border border-primary',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible'
                          )}
                        >
                          <Check className={cn('h-4 w-4 text-background')} />
                        </div>
                        <span>
                          {t(
                            `packagingSuppliers:packagingItems.lastUpdatedFilterWindow.${window}`
                          )}
                        </span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </div>
            ))}
            {isFiltered && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onValueChange(undefined)
                    }}
                    className='justify-center text-center'
                  >
                    {t('common:dataTable.clearFilters')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
